import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import {
  fromUTCtoZoneFormatted,
  notifyParent,
  storeGet,
  STORE_KEY_LIST
} from 'mailup-react-helpers'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'

import {
  TooltipUtils,
  SkeletonLoader,
  Badge,
  BADGE_KIND_SECONDARY,
  BADGE_TYPE_DEFAULT,
  BADGE_TYPE_SUCCESS,
  BADGE_TYPE_INFO,
  BADGE_TYPE_ERROR,
  BADGE_TYPE_WARNING,
  Grid,
  GridCell,
  Flex,
  FlexItem,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  LINK_VARIANT_EXTRALIGHT,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  Accordion,
  ACCORDION_VARIANT_LIGHT,
  TruncateAndTooltip,
  IconTrophy2,
  IconPaperPlane,
  DataListExtraColumn,
  Divider
} from 'mailup-web-components'

import {
  messageDetailsState,
  resultsState,
  rowState
} from 'Pages/AbTestList/recoil/state/results'
import {
  getMessagesDetailsCountCallback,
  getMessagesSubjectCallback
} from 'Pages/AbTestList/recoil/callbacks/callbacks'

import {
  ABTEST_STATUS_COMPLETED,
  ABTEST_STATUS_CONFIGURATION,
  ABTEST_STATUS_ENQUEUING,
  ABTEST_STATUS_POST_ENQUEUING,
  ABTEST_STATUS_PRE_ENQUEUING,
  ABTEST_STATUS_SCHEDULING,
  ABTEST_STATUS_SENDING_OF_TEST_MESSAGES_COMPLETED,
  ABTEST_STATUS_SENDING_OF_TEST_MESSAGES_STARTED,
  ABTEST_STATUS_SENDING_OF_WINNER_MESSAGE_STARTED,
  ABTEST_STATUS_STOP_DURING_TEST,
  ABTEST_STATUS_STOP_DURING_WINNER,
  ABTEST_STATUS_WAITING_FOR_SENDING_OF_TEST_MESSAGES,
  ABTEST_STATUS_WINNER_MESSAGE_DEFINED,
  ABTEST_STATUS_WINNER_MESSAGE_SCHEDULED
} from 'Api/beABTestApi'
import {
  ABTEST_LIST_TYPE_100,
  ABTEST_LIST_TYPE_MANUAL,
  ABTEST_LIST_TYPE_AUTO,
  ABTEST_SENDING_TYPE_WINNING,
  ABTEST_LIST_CRITERIA_CLICK_RATE,
  ABTEST_LIST_CRITERIA_OPEN_RATE
} from 'Constants'
import styled from '@emotion/styled'
import ActionsMenu from './ActionsMenu'

const Result = ({ info, messages, aggregateReport, recoverable }) => {
  const intl = useIntl()

  const [accordionState, setAccordionState] = useState(false)

  return (
    <>
      <Flex flexDirection='column' width='100%'>
        <FlexItem margin='0 0 16px 0'>
          <Flex justifyContent='space-between' flexDirection='row'>
            <Grid cols={12}>
              <GridCell span={3}>
                <Flex flexDirection='column'>
                  <TitleComponent {...info} messages={messages} />
                  <NotesComponent {...info} />
                  <Accordion
                    onExpandCollapse={() => {
                      setAccordionState(!accordionState)
                    }}
                    defaultExpanded={accordionState}
                    variant={ACCORDION_VARIANT_LIGHT}
                    data-mu-test='more-details'
                    label={intl.formatMessage({
                      id: 'Load.MailupCommon.MoreDetails'
                    })}
                  />
                </Flex>
              </GridCell>

              <GridCell span={6}>
                <Flex flexDirection='column'>
                  <MessagesListComponent messages={messages} info={info} />
                </Flex>
              </GridCell>

              <GridCell span={3}>
                <StatusComponent {...info} messages={messages} />
              </GridCell>
            </Grid>

            <DataListExtraColumn after width='26px'>
              <ActionsComponent
                aggregateReport={aggregateReport}
                info={info}
                recoverable={recoverable}
              />
            </DataListExtraColumn>
          </Flex>
        </FlexItem>
        {!accordionState && <AccordionComponent {...info} />}
      </Flex>
    </>
  )
}

export default Result

// used components

const TitleComponent = ({
  Nome: nome,
  Stato: stato,
  ABTestID: idABTest,
  messages
}) => {
  const history = useHistory()

  const [
    { detailsCount, numberOfMessageLoaded, numberOfMessage },
    setRowState
  ] = useRecoilState(rowState(idABTest))

  const getMessagesDetailsCount = useRecoilCallback(
    getMessagesDetailsCountCallback,
    []
  )

  useEffect(() => {
    if (numberOfMessageLoaded === numberOfMessage) {
      getMessagesDetailsCount(messages, idABTest)
    }
  }, [numberOfMessageLoaded, numberOfMessage])

  useEffect(() => {
    detailsCount >= 0 &&
      setRowState(prev => ({
        ...prev,
        titleLoaded: true,
        detailsCount
      }))
  }, [detailsCount])

  const handleEdit = () => {
    history.push(`/messages/email/abtest/${idABTest}`)
  }

  const handleManage = () => {
    history.push(`/messages/email/abtestmanage/${idABTest}`)
  }

  const editOrManage = () => {
    if (
      [
        ABTEST_STATUS_CONFIGURATION,
        ABTEST_STATUS_PRE_ENQUEUING,
        ABTEST_STATUS_ENQUEUING,
        ABTEST_STATUS_POST_ENQUEUING,
        ABTEST_STATUS_SCHEDULING
      ].includes(stato)
    ) {
      return (
        <Link onClick={handleEdit} variant={LINK_VARIANT_HIGHLIGHT}>
          {nome}
        </Link>
      )
    } else if (
      [
        ABTEST_STATUS_WAITING_FOR_SENDING_OF_TEST_MESSAGES,
        ABTEST_STATUS_SENDING_OF_TEST_MESSAGES_STARTED,
        ABTEST_STATUS_WINNER_MESSAGE_DEFINED,
        ABTEST_STATUS_WINNER_MESSAGE_SCHEDULED,
        ABTEST_STATUS_SENDING_OF_WINNER_MESSAGE_STARTED,
        ABTEST_STATUS_SENDING_OF_TEST_MESSAGES_COMPLETED,
        ABTEST_STATUS_COMPLETED,
        ABTEST_STATUS_STOP_DURING_TEST,
        ABTEST_STATUS_STOP_DURING_WINNER
      ].includes(stato)
    ) {
      return (
        <Link onClick={handleManage} variant={LINK_VARIANT_HIGHLIGHT}>
          {nome}
        </Link>
      )
    } else {
      return <Text variant={TEXT_VARIANT_BOLD}>{nome}</Text>
    }
  }

  const simpleTitle = () => {
    return <Text variant={TEXT_VARIANT_BOLD}>{nome}</Text>
  }

  return (
    <>
      {detailsCount === -1 && <SkeletonLoader />}
      {detailsCount === 0 && editOrManage()}
      {detailsCount > 0 && simpleTitle()}
    </>
  )
}

const NotesComponent = ({ LastModificationDate: lastUpdate }) => {
  const intl = useIntl()
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)

  return (
    <Text variant={TEXT_VARIANT_LIGHT}>
      {intl.formatMessage({ id: 'Load.MailupCommon.LastUpdated' })}{' '}
      {fromUTCtoZoneFormatted(lastUpdate, TimeZoneCode, 'YYYY-MM-DD')}
    </Text>
  )
}

const AccordionComponent = ({
  PercTest: percTest,
  TipoTest: tipoTest,
  Criterio: criterio,
  TestDateCTZScheduled: testDateScheduled,
  WinnerDateCTZScheduled: winnerDateScheduled
}) => {
  const intl = useIntl()

  return (
    <Flex>
      <Grid>
        <GridCell span={3} />
        <GridCell span={9}>
          <Flex flexDirection='column' width='100%'>
            <FlexItem margin='0 0 16px 0' flexDirection='column'>
              <Divider />
            </FlexItem>
            <FlexItem>
              <Grid>
                <GridCell width='33%'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({ id: 'Load.AbTest.Criteria' })}
                  </Text>
                  <Text variant={TEXT_VARIANT_LIGHT}>
                    {percTest} %{' '}
                    {intl.formatMessage({ id: 'Load.AbTest.OfRecipients' })}{' '}
                    {tipoTest === ABTEST_LIST_TYPE_AUTO &&
                      criterio === ABTEST_LIST_CRITERIA_CLICK_RATE && (
                        <>
                          |{' '}
                          {intl.formatMessage({
                            id: 'Load.ABTestWizard.Criteria1'
                          })}
                        </>
                      )}
                    {tipoTest === ABTEST_LIST_TYPE_AUTO &&
                      criterio === ABTEST_LIST_CRITERIA_OPEN_RATE && (
                        <>
                          |{' '}
                          {intl.formatMessage({
                            id: 'Load.ABTestWizard.Criteria2'
                          })}
                        </>
                      )}
                  </Text>
                </GridCell>
                <GridCell width='33%'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({ id: 'Load.AbTest.TestStartDate' })}
                  </Text>
                  <Text variant={TEXT_VARIANT_LIGHT}>
                    {testDateScheduled
                      ? testDateScheduled
                          .replace('T', ' ')
                          .replace('Z', '')
                          .split('.')[0]
                      : '-'}
                  </Text>
                </GridCell>
                <GridCell width='33%'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({ id: 'Load.AbTest.InvioVincente' })}
                  </Text>
                  <Text variant={TEXT_VARIANT_LIGHT}>
                    {winnerDateScheduled != null && (
                      <>
                        <Text variant={TEXT_VARIANT_LIGHT}>
                          {intl.formatMessage({
                            id: 'Load.AbTest.Scheduled'
                          })}
                        </Text>
                        <Text variant={TEXT_VARIANT_LIGHT}>
                          {winnerDateScheduled
                            .replace('T', ' ')
                            .replace('Z', '')}
                        </Text>
                      </>
                    )}
                    {winnerDateScheduled == null &&
                      ((tipoTest === ABTEST_LIST_TYPE_AUTO &&
                        intl.formatMessage({ id: 'Load.AbTest.TypeAuto' })) ||
                        (tipoTest === ABTEST_LIST_TYPE_MANUAL &&
                          intl.formatMessage({
                            id: 'Load.AbTest.TypeManual'
                          })) ||
                        (tipoTest === ABTEST_LIST_TYPE_100 &&
                          intl.formatMessage({
                            id: 'Load.MailupCommon.No'
                          })))}
                  </Text>
                </GridCell>
              </Grid>
            </FlexItem>
          </Flex>
        </GridCell>
      </Grid>
      <DataListExtraColumn after width='26px' />
    </Flex>
  )
}

const MessagesListComponent = ({ messages, info }) => {
  return (
    <>
      {messages.map(({ IdMessaggio, TipoInvio }, index) => (
        <FlexItem padding='0 20px 0 0' width='100%' key={index}>
          <Subject {...info} idMessaggio={IdMessaggio} tipoInvio={TipoInvio} />
        </FlexItem>
      ))}
    </>
  )
}

const Subject = ({ idMessaggio, tipoInvio, ABTestID: idAbTest }) => {
  const intl = useIntl()
  const { subject, loading, loaded } = useRecoilValue(
    messageDetailsState(idMessaggio)
  )

  useEffect(() => {
    setTimeout(() => {
      TooltipUtils.rebuild()
    })
  }, [loaded])

  const getMessageDetails = useRecoilCallback(getMessagesSubjectCallback, [])

  useEffect(() => {
    !loaded && getMessageDetails(idMessaggio, idAbTest)
  }, [idMessaggio, loaded])

  const openPopup = idMessage => {
    notifyParent('openPreviewMainFrame', [idMessage])
  }

  return (
    <>
      {loading && tipoInvio !== ABTEST_SENDING_TYPE_WINNING && (
        <>
          <SkeletonLoader width='85%' />
        </>
      )}
      {loaded && tipoInvio !== ABTEST_SENDING_TYPE_WINNING && (
        <>
          {subject !== null ? (
            <FlexItem maxWidth='550px' minWidth='0'>
              <Link
                variant={LINK_VARIANT_EXTRALIGHT}
                onClick={() => openPopup(idMessaggio)}
                data-mu-test={'abTestListPreview__msg'}
              >
                <TruncateAndTooltip
                  text={
                    intl.formatMessage({ id: 'Load.MailupCommon.Id' }) +
                    ' ' +
                    idMessaggio +
                    ' - ' +
                    subject
                  }
                />
              </Link>
            </FlexItem>
          ) : (
            <>
              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({ id: 'Load.MailupCommon.Id' })}{' '}
                {idMessaggio}
                {'- '}
                {intl.formatMessage({ id: 'Load.MailupCommon.Deleted' })}
              </Text>
            </>
          )}
        </>
      )}
      {loaded && tipoInvio === ABTEST_SENDING_TYPE_WINNING && subject !== null && (
        <Flex width='100%' justifyContent='space-between'>
          <FlexItem maxWidthidth='550px' minWidth='0'>
            <Link
              variant={LINK_VARIANT_EXTRALIGHT}
              onClick={() => openPopup(idMessaggio)}
              data-mu-test={'abTestListPreview__winning-msg'}
            >
              <TruncateAndTooltip
                text={
                  intl.formatMessage({ id: 'Load.MailupCommon.Id' }) +
                  ' ' +
                  idMessaggio +
                  ' - ' +
                  subject
                }
              />
            </Link>
          </FlexItem>
          <FlexItem margin='0 0 0 8px'>
            <IconTrophy2
              data-tip={intl.formatMessage({
                id: 'Load.AbTest.WinnerMessage'
              })}
            />
          </FlexItem>
        </Flex>
      )}
    </>
  )
}

const StatusComponent = ({ ABTestID: idAbTest, Stato: stato, messages }) => {
  const intl = useIntl()
  const history = useHistory()

  const { sendingfailedIds } = useRecoilValue(resultsState)

  const statusCheck = stato => {
    switch (stato) {
      case 0:
      case 3:
      case 6:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusDraft' })}
            type={BADGE_TYPE_DEFAULT}
          />
        )
      case 4:
      case 5:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusLoadingTest' })}
            type={BADGE_TYPE_WARNING}
          />
        )
      case 7:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusScheduled' })}
            type={BADGE_TYPE_INFO}
          />
        )
      case 1:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusTestRunning' })}
            type={BADGE_TYPE_WARNING}
          />
        )
      case 11:
      case 12:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusTestEnded' })}
            type={BADGE_TYPE_SUCCESS}
          />
        )
      case 2:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusWinnerRunning' })}
            type={BADGE_TYPE_WARNING}
          />
        )
      case 14:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusFinished' })}
            type={BADGE_TYPE_SUCCESS}
          />
        )
      case 13:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({
              id: 'Load.AbTest.StatusWinnerScheduled'
            })}
            type={BADGE_TYPE_INFO}
          />
        )
      case 15:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusTestAborted' })}
            type={BADGE_TYPE_ERROR}
          />
        )
      case 16:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({
              id: 'Load.AbTest.StatusWinnerMailingAborted'
            })}
            type={BADGE_TYPE_ERROR}
          />
        )
      case -1:
      case -2:
        return (
          <Badge
            kind={BADGE_KIND_SECONDARY}
            text={intl.formatMessage({ id: 'Load.AbTest.StatusLoadingFailed' })}
            type={BADGE_TYPE_ERROR}
          />
        )
      default:
        return null
    }
  }

  const failedCount = []
  messages.forEach(e => {
    sendingfailedIds.includes(e.IdInvio) && failedCount.push(e.IdInvio)
  })

  const failed = failedCount.length > 0

  const handleResultsClick = () => {
    history.push(`/messages/email/abtestmanage/${idAbTest}`)
  }

  const handleRedirect = () => {
    history.push(`/messages/email/sendstatus/campaigns`)
  }

  return (
    <Flex
      flexDirection='column'
      alignItems='flex-start'
      justifyContent='center'
      padding=' 0 20px 0 0'
    >
      <FlexItem>
        <Flex flexWrap='wrap' alignItems='flex-start'>
          {statusCheck(stato)}
          {failed && (
            <FlexItem margin='0 8px'>
              <Link onClick={handleRedirect}>
                <FailedIcon
                  data-tip={intl.formatMessage({
                    id: 'Load.AbTest.MessageisFailedTooltip'
                  })}
                />
              </Link>
            </FlexItem>
          )}
        </Flex>
      </FlexItem>
      {stato === 12 && (
        <FlexItem margin='8px 0 0 0' maxWidth='10em'>
          <Link variant={LINK_VARIANT_HIGHLIGHT} onClick={handleResultsClick}>
            {intl.formatMessage({ id: 'Load.AbTest.GuardaRisultati' })}
          </Link>
        </FlexItem>
      )}
    </Flex>
  )
}

const ActionsComponent = ({ info, aggregateReport, recoverable }) => {
  const { detailsCount, titleLoaded } = useRecoilValue(rowState(info.ABTestID))

  return (
    <>
      {!titleLoaded && <SkeletonLoader />}
      {titleLoaded && (
        <ActionsMenu
          info={info}
          aggregateReport={aggregateReport}
          recoverable={recoverable}
          count={detailsCount}
        />
      )}
    </>
  )
}

const FailedIcon = styled(IconPaperPlane)`
  border-radius: 100%;
  padding: 7px 8px 7px 6px;
  background-color: var(--sunset-orange);
  svg {
    height: 18px;
    width: 18px;
    path {
      fill: #ffffff !important;
    }
  }
`

// proptypes
Result.propTypes = {
  info: PropTypes.object,
  messages: PropTypes.array,
  aggregateReport: PropTypes.any,
  recoverable: PropTypes.any
}

TitleComponent.propTypes = {
  messages: PropTypes.array,
  Nome: PropTypes.string,
  Stato: PropTypes.any,
  ABTestID: PropTypes.number
}

NotesComponent.propTypes = {
  LastModificationDate: PropTypes.any
}

AccordionComponent.propTypes = {
  PercTest: PropTypes.any,
  Criterio: PropTypes.any,
  TipoTest: PropTypes.any,
  TestDateCTZScheduled: PropTypes.any,
  WinnerDateCTZScheduled: PropTypes.any
}

MessagesListComponent.propTypes = {
  messages: PropTypes.array,
  info: PropTypes.object
}

Subject.propTypes = {
  idMessaggio: PropTypes.number,
  tipoInvio: PropTypes.any,
  ABTestID: PropTypes.number
}

StatusComponent.propTypes = {
  messages: PropTypes.array,
  Stato: PropTypes.any,
  ABTestID: PropTypes.number
}

ActionsComponent.propTypes = {
  info: PropTypes.object,
  aggregateReport: PropTypes.any,
  recoverable: PropTypes.any
}
